import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslocoDirective, TranslocoService, translateSignal } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { SimpleTableComponent } from '../../ui/simple-table/simple-table.component';
import { SimpleTableColumn } from '../../ui/simple-table/simple-table.models';
import { ConfirmationService } from '../../../services/confirmation.service';
import { PanelHostService } from '../../../services/panel-host.service';
import { TripFinanceService } from '../../../services/trip-finance.service';
import { TripService } from '../../../services/trip.service';
import { NewBudgetWithTypeLabel } from '../../../models/finance-models';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-budget-panel',
  templateUrl: './budget-panel.component.html',
  styleUrls: ['./budget-panel.component.css'],
  imports: [TranslocoDirective, SimpleTableComponent, FaIconComponent, CurrencyPipe, ContentContainerComponent, PanelBodyComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  budget = signal<NewBudgetWithTypeLabel[]>([]);
  perPersonBasis = signal(0);
  groupExpensesTotal = signal(0);
  loading = signal(false);

  translationSignals = {
    categoryHeader: translateSignal(marker('trip-finances.panels.budget.category')),
    descriptionHeader: translateSignal(marker('trip-finances.panels.budget.description-column')),
    amountHeader: translateSignal(marker('trip-finances.panels.budget.amount')),
    amountTypeHeader: translateSignal(marker('trip-finances.panels.budget.amount-type')),
    dueHeader: translateSignal(marker('trip-finances.panels.budget.due-date')),
    lineTotalHeader: translateSignal(marker('trip-finances.panels.budget.line-total')),
  };

  columns = computed<SimpleTableColumn<NewBudgetWithTypeLabel>[]>(() => [
    { header: this.translationSignals.descriptionHeader(), dataKey: 'description', type: 'text', enableSorting: true },
    { header: this.translationSignals.categoryHeader(), dataKey: 'typeLabel', type: 'translation-key', enableSorting: true },
    { header: this.translationSignals.amountHeader(), dataKey: 'amount', type: 'currency', enableSorting: true },
    { header: this.translationSignals.amountTypeHeader(), dataKey: 'amountTypeLabel', type: 'translation-key', enableSorting: true },
    { header: this.translationSignals.dueHeader(), dataKey: 'due', type: 'date', enableSorting: true },
    {
      header: this.translationSignals.lineTotalHeader(),
      dataKey: 'lineTotal',
      type: 'currency',
      enableSorting: true,
    },
  ]);

  constructor() {
    effect(() => {
      this.tripFinanceService.moneyIn.value();
      if (this.panelHost.activePanel() === 'budget') {
        this.loadBudget();
      }
    });
  }

  loadBudget(): void {
    const tripId = this.tripService.currentTrip.value()?.id;
    if (!tripId) {
      return;
    }

    this.loading.set(true);
    this.tripFinanceService
      .getBudget(tripId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (envelope) => {
          this.budget.set(envelope.items);
          this.perPersonBasis.set(envelope.perPersonBasis);
          this.groupExpensesTotal.set(envelope.groupExpensesTotal);
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.budget.load-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  addBudgetItem(): void {
    this.panelHost.openTripFinance(
      { mode: 'budget-item', action: 'create' },
      this.transloco.translate(marker('trip-finances.panels.budget-item.title-add')),
    );
  }

  editBudgetItem(row: NewBudgetWithTypeLabel): void {
    this.panelHost.openTripFinance(
      { mode: 'budget-item', action: 'edit', record: row },
      this.transloco.translate(marker('trip-finances.panels.budget-item.title-edit')),
    );
  }

  async deleteBudgetItem(row: NewBudgetWithTypeLabel): Promise<void> {
    const confirmed = await this.confirmationService.confirmDelete({
      header: this.transloco.translate(marker('trip-finances.confirm-delete.budget-item.header')),
      message: this.transloco.translate(marker('trip-finances.confirm-delete.budget-item.message')),
      confirmText: this.transloco.translate(marker('trip-finances.confirm-delete.confirm')),
      cancelText: this.transloco.translate(marker('trip-finances.confirm-delete.cancel')),
    });

    if (!confirmed) {
      return;
    }

    const tripId = this.tripService.currentTrip.value()?.id;
    if (!tripId) {
      return;
    }

    this.tripFinanceService.deleteBudgetItem(tripId, row.id).subscribe({
      next: async () => {
        this.loadBudget();
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('trip-finances.panels.budget.delete-success')),
          duration: 4000,
          color: 'success',
        });
        await toast.present();
      },
      error: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('trip-finances.panels.save-error')),
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }
}
